import { AIController } from '../../ai/aiController';
import type { Command, CommandEnvelope } from '../../engine/simulation/commands';
import { applySaveInPlace, saveSimulation, type Simulation } from '../../engine/simulation/simulation';
import type { CommandTransport, NetPlayerRow } from '../transport';
import type { NetMessage } from '../protocol';
import type { NetSession } from '../session';

export const HASH_INTERVAL = 20;

/**
 * Deterministic lockstep over a NetSession.
 *
 * Every human player sends exactly one command batch per simulation tick,
 * targeted `inputDelay` ticks in the future (empty batches are sent too, as
 * heartbeats). A tick executes only when batches from every active human for
 * that tick have arrived, so all peers apply identical commands at identical
 * ticks. State hashes are exchanged every HASH_INTERVAL ticks; a mismatch
 * pauses the game and the coordinator (lowest active player) re-synchronises
 * everyone from its authoritative state.
 *
 * Disconnects: survivors exchange the last tick they hold from the lost player;
 * the maximum + 1 becomes the agreed takeover tick, at which every peer
 * deterministically hands the player to the AI.
 */
export class LockstepTransport implements CommandTransport {
  readonly networked = true;
  private sim: Simulation | null = null;
  private seq = 0;
  private pending: Command[] = [];
  private batches = new Map<number, Map<number, CommandEnvelope[]>>();
  private droppedFrom = new Map<number, number>();
  private lostReports = new Map<number, Map<string, { last: number }>>();
  private remoteHashes = new Map<number, Map<number, number>>();
  private desyncMsg: string | null = null;
  private desyncTick = -1;
  private waitingSince = 0;
  private waitingFor: number[] = [];
  /** Waits laid at each player's door: [time, ms] pairs from the last 30 seconds. */
  private heldUp = new Map<number, [number, number][]>();
  private lastWaitCheck = 0;
  private pingTimer: ReturnType<typeof setInterval> | undefined;
  stats = { bytesSent: 0, batchesSent: 0, batchesReceived: 0, maxWaitMs: 0, desyncs: 0, resyncs: 0 };
  onTakeover: ((player: number) => void) | null = null;
  onChat: ((player: number, text: string, x?: number, z?: number) => void) | null = null;

  /** Chat (all players or allies only) and map flares; not part of the simulation. */
  sendChat(text: string, alliesOnly: boolean, x?: number, z?: number): void {
    this.session.broadcast({ type: 'chat', from: this.session.localId, player: this.localPlayer, text: text.slice(0, 200), allies: alliesOnly, x, z });
  }

  constructor(
    private session: NetSession,
    readonly localPlayer: number,
    private peers: (string | null)[],
    readonly inputDelay: number,
  ) {
    session.onGameMessage = (m) => this.receive(m);
    session.onPeerLost = (id) => this.peerLost(id);
    this.pingTimer = globalThis.setInterval(() => session.pingAll(), 2000);
  }

  private humans(): number[] {
    const out: number[] = [];
    this.peers.forEach((p, i) => p !== null && out.push(i));
    return out;
  }

  private activeAt(player: number, tick: number): boolean {
    const d = this.droppedFrom.get(player);
    return d === undefined || tick < d;
  }

  attach(sim: Simulation): void {
    this.sim = sim;
    this.seq = Math.max(0, (sim.world.players[this.localPlayer]?.lastSeq ?? -1) + 1);
    // Ticks before the first possible batch are implicitly empty for everyone.
    for (const p of this.humans()) for (let t = sim.tick + 1; t <= sim.tick + this.inputDelay; t++) this.store(p, t, []);
  }

  private store(player: number, tick: number, cmds: CommandEnvelope[]): void {
    let m = this.batches.get(player);
    if (!m) this.batches.set(player, (m = new Map()));
    if (!m.has(tick)) m.set(tick, cmds);
  }

  submit(cmd: Command): void {
    this.pending.push(cmd);
  }

  canAdvance(tick: number): boolean {
    if (!this.sim || this.desyncMsg) return false;
    const missing: number[] = [];
    for (const p of this.humans()) if (this.activeAt(p, tick) && !this.batches.get(p)?.has(tick)) missing.push(p);
    if (missing.length) {
      const now = performance.now();
      if (!this.waitingFor.length) this.waitingSince = now;
      // Each player we are waiting for is charged the time since the last check.
      if (this.lastWaitCheck) for (const p of missing) this.chargeWait(p, now, now - this.lastWaitCheck);
      this.lastWaitCheck = now;
      this.waitingFor = missing;
      return false;
    }
    this.lastWaitCheck = 0;
    if (this.waitingFor.length) this.stats.maxWaitMs = Math.max(this.stats.maxWaitMs, performance.now() - this.waitingSince);
    this.waitingFor = [];
    // Deterministic AI takeover exactly at the agreed tick.
    for (const [p, k] of this.droppedFrom) {
      if (k === tick && !this.sim.ais[p]) {
        this.sim.ais[p] = new AIController(p, 'standard', 'balanced', this.sim.config.seed ^ (p * 7919));
        this.sim.world.players[p].isAI = true;
        this.onTakeover?.(p);
      }
    }
    return true;
  }

  afterTick(tick: number): void {
    const sim = this.sim!;
    // Send our batch for tick + delay (possibly empty: doubles as a heartbeat).
    const target = tick + this.inputDelay;
    const envs: CommandEnvelope[] = this.pending.splice(0, 64).map((cmd) => ({ tick: target, player: this.localPlayer, seq: this.seq++, cmd }));
    this.store(this.localPlayer, target, envs);
    for (const e of envs) sim.schedule(e);
    const msg: NetMessage = { type: 'batch', from: this.session.localId, player: this.localPlayer, tick: target, cmds: envs };
    this.session.broadcast(msg);
    this.stats.batchesSent++;
    this.stats.bytesSent += JSON.stringify(msg).length;
    if (tick % HASH_INTERVAL === 0) {
      const h = sim.hash();
      this.session.broadcast({ type: 'hash', from: this.session.localId, player: this.localPlayer, tick, hash: h });
      this.checkHashes(tick, this.localPlayer, h);
    }
    // Garbage-collect old batches.
    if (tick % 200 === 0) for (const m of this.batches.values()) for (const t of m.keys()) if (t < tick - 400) m.delete(t);
  }

  private ownHashes = new Map<number, number>();

  private checkHashes(tick: number, player: number, hash: number): void {
    if (tick <= this.desyncTick) return;
    if (player === this.localPlayer) this.ownHashes.set(tick, hash);
    else {
      let m = this.remoteHashes.get(tick);
      if (!m) this.remoteHashes.set(tick, (m = new Map()));
      m.set(player, hash);
    }
    const mine = this.ownHashes.get(tick);
    const theirs = this.remoteHashes.get(tick);
    if (mine === undefined || !theirs) return;
    for (const [p, h] of theirs) {
      if (h !== mine) {
        this.desyncTick = tick;
        this.stats.desyncs++;
        this.desyncMsg = `Desynchronisation detected at tick ${tick} (player ${p + 1}). Resynchronising…`;
        if (this.isCoordinator()) this.sendResync();
        else this.session.broadcast({ type: 'resyncRequest', from: this.session.localId, tick });
        return;
      }
    }
    this.remoteHashes.delete(tick - HASH_INTERVAL * 10);
    this.ownHashes.delete(tick - HASH_INTERVAL * 10);
  }

  private isCoordinator(): boolean {
    const sim = this.sim!;
    const active = this.humans().filter((p) => this.activeAt(p, sim.tick + 1));
    return active[0] === this.localPlayer;
  }

  private sendResync(): void {
    const sim = this.sim!;
    const save = saveSimulation(sim);
    this.session.broadcast({ type: 'resync', from: this.session.localId, tick: sim.tick, save });
    // The coordinator's own state is authoritative; it resumes immediately.
    this.desyncMsg = null;
  }

  private receive(m: NetMessage): void {
    const sim = this.sim;
    if (!sim) return;
    switch (m.type) {
      case 'batch': {
        // A peer may only send commands for its own player slot.
        if (this.peers[m.player] !== m.from || m.player === this.localPlayer) return;
        if (!this.activeAt(m.player, m.tick)) return;
        if (m.tick <= sim.tick) return; // too late (should not happen in lockstep)
        const envs = m.cmds.filter((e) => e.player === m.player && e.tick === m.tick);
        this.store(m.player, m.tick, envs);
        for (const e of envs) sim.schedule(e);
        this.stats.batchesReceived++;
        break;
      }
      case 'hash':
        if (this.peers[m.player] === m.from) this.checkHashes(m.tick, m.player, m.hash);
        break;
      case 'resyncRequest':
        if (this.isCoordinator()) this.sendResync();
        break;
      case 'resync': {
        const coord = this.humans().filter((p) => this.activeAt(p, sim.tick + 1))[0];
        if (this.peers[coord] !== m.from) return;
        applySaveInPlace(sim, m.save);
        this.stats.resyncs++;
        // Re-apply every batch we hold beyond the resync tick (dedup by seq).
        for (const [, map] of this.batches) for (const [t, envs] of map) if (t > sim.tick) for (const e of envs) sim.schedule(e);
        this.remoteHashes.clear();
        this.ownHashes.clear();
        this.desyncTick = sim.tick;
        this.desyncMsg = null;
        break;
      }
      case 'peerLost':
        this.recordLost(m.lost, m.from, m.last, m.batches);
        break;
      case 'chat': {
        if (this.peers[m.player] !== m.from) return;
        const sim = this.sim;
        if (m.allies && sim && sim.world.areEnemies(m.player, this.localPlayer)) return;
        this.onChat?.(m.player, m.text, m.x, m.z);
        break;
      }
      default:
        break;
    }
  }

  private lastTickOf(player: number): number {
    let last = -1;
    for (const t of this.batches.get(player)?.keys() ?? []) last = Math.max(last, t);
    return last;
  }

  private peerLost(peerId: string): void {
    const p = this.peers.indexOf(peerId);
    if (p < 0 || p === this.localPlayer || this.droppedFrom.has(p)) return;
    const last = this.lastTickOf(p);
    const recent: { tick: number; cmds: CommandEnvelope[] }[] = [];
    for (const [t, c] of this.batches.get(p) ?? []) if (t > (this.sim?.tick ?? 0) - 2) recent.push({ tick: t, cmds: c });
    this.session.broadcast({ type: 'peerLost', from: this.session.localId, lost: p, last, batches: recent });
    this.recordLost(p, this.session.localId, last, recent);
  }

  private recordLost(p: number, reporter: string, last: number, batches: { tick: number; cmds: CommandEnvelope[] }[]): void {
    if (this.droppedFrom.has(p) || !Number.isInteger(p) || this.peers[p] === null) return;
    for (const b of batches) {
      const envs = b.cmds.filter((e) => e.player === p && e.tick === b.tick);
      if (b.tick > (this.sim?.tick ?? 0)) {
        this.store(p, b.tick, envs);
        for (const e of envs) this.sim?.schedule(e);
      }
    }
    let r = this.lostReports.get(p);
    if (!r) this.lostReports.set(p, (r = new Map()));
    r.set(reporter, { last });
    // All surviving connected humans must report before we agree on the tick.
    const survivors = this.humans().filter((h) => h !== p && this.activeAt(h, Infinity)).map((h) => this.peers[h]!);
    const reporters = survivors.filter((id) => id === this.session.localId || this.session.links.has(id) || this.session.relay);
    if (!reporters.every((id) => r!.has(id))) return;
    let k = -1;
    for (const v of r.values()) k = Math.max(k, v.last);
    // No survivor can have executed beyond the last batch it holds, so k + 1 is safe and identical everywhere.
    this.droppedFrom.set(p, k + 1);
  }

  status(): string | null {
    const sim = this.sim;
    const peers = this.session.links.size;
    if (this.waitingFor.length && performance.now() - this.waitingSince > 400) {
      const names = this.waitingFor.map((p) => sim?.world.players[p]?.name ?? `P${p + 1}`).join(', ');
      return `Waiting for ${names}…`;
    }
    let rtt = 0;
    for (const l of this.session.links.values()) rtt = Math.max(rtt, l.rtt);
    // The one holding the game up, if anyone is (the connection panel has the details).
    const lag = this.players().filter((r) => r.status === 'lagging' && !r.you).sort((a, b) => b.heldUpMs - a.heldUpMs)[0];
    return `Online · ${peers} peer${peers === 1 ? '' : 's'} · ${Math.round(rtt)} ms · delay ${this.inputDelay * 50} ms${lag ? ` · lagging: ${lag.name}` : ''}`;
  }

  desync(): string | null {
    return this.desyncMsg;
  }

  private chargeWait(p: number, now: number, ms: number): void {
    let l = this.heldUp.get(p);
    if (!l) this.heldUp.set(p, (l = []));
    l.push([now, Math.min(ms, 1000)]);
    while (l.length && l[0][0] < now - 30_000) l.shift();
  }

  setLocalFps(fps: number): void {
    this.session.localPerf = { fps: Math.round(fps), tick: this.sim?.tick ?? 0 };
  }

  /**
   * Every human player's connection, for the connection panel. A player is lagging when the game
   * has waited for them for more than 1.5 s in the last 30 s, or their game runs under 20 fps; slow
   * above 0.3 s of waiting, a 250 ms ping, or under 40 fps. Waits are what the others feel: in
   * lockstep nobody moves until the slowest player's orders arrive, whether their connection or
   * their computer is the cause, and the frame rate and ping show which.
   */
  players(): NetPlayerRow[] {
    const sim = this.sim;
    if (!sim) return [];
    const now = performance.now();
    const rows: NetPlayerRow[] = [];
    for (const p of this.humans()) {
      const pl = sim.world.players[p];
      const id = this.peers[p]!;
      const you = p === this.localPlayer;
      const link = you ? undefined : this.session.links.get(id);
      const perf = you ? { fps: this.session.localPerf.fps, tick: sim.tick, at: now } : this.session.peerPerf.get(id);
      const fresh = perf && now - perf.at < 6000 ? perf : undefined;
      const heldUpMs = Math.round((this.heldUp.get(p) ?? []).filter(([t]) => t >= now - 30_000).reduce((a, [, ms]) => a + ms, 0));
      const pingMs = link ? Math.round(link.rtt) : null;
      // Their tick when they pinged, carried forward at game speed to now.
      const behindSec = fresh && !you ? Math.max(0, (sim.tick - (fresh.tick + ((now - fresh.at) / 1000) * 20)) / 20) : null;
      const fps = fresh ? fresh.fps : null;
      let status: NetPlayerRow['status'] = 'good';
      if (!this.activeAt(p, sim.tick + 1)) status = 'left';
      else if (heldUpMs > 1500 || (fps !== null && fps > 0 && fps < 20)) status = 'lagging';
      else if (heldUpMs > 300 || (pingMs !== null && pingMs > 250) || (fps !== null && fps > 0 && fps < 40)) status = 'slow';
      rows.push({ player: p, name: pl?.name ?? `Player ${p + 1}`, color: '#' + (pl?.color ?? 0xffffff).toString(16).padStart(6, '0'), you, pingMs, fps, behindSec, heldUpMs, status });
    }
    return rows;
  }

  dispose(): void {
    clearInterval(this.pingTimer);
    this.session.close();
  }
}
